import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { pmApi } from '../../api/pm.js';

export default function Releases() {
  const { key, id } = useParams();
  const [project, setProject] = useState(null);
  useEffect(() => {
    if (key) pmApi.getProject(key).then((d) => setProject(d?.project));
    else setProject(null);
  }, [key]);

  if (id) return <ReleaseDetail releaseId={id} project={project} />;
  if (key && !project) return <div className="muted">Loading…</div>;
  if (!project && !key) return <GlobalReleases />;
  return <ReleaseList project={project} />;
}

function GlobalReleases() {
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState(null);
  useEffect(() => { pmApi.projects().then((d) => { setProjects(d?.items || []); if (d?.items?.[0]) setPid(d.items[0].id); }); }, []);
  if (!projects.length) return <div className="muted">Create a project first.</div>;
  const project = projects.find((p) => p.id === pid);
  return (
    <section>
      <header className="page-header">
        <h1>Releases</h1>
        <select value={pid || ''} onChange={(e) => setPid(e.target.value)}>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.key} · {p.name}</option>)}
        </select>
      </header>
      {project && <ReleaseList project={project} />}
    </section>
  );
}

function ReleaseList({ project }) {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [issues, setIssues] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [pickedIssues, setPickedIssues] = useState(new Set());

  function refresh() { pmApi.releases(project.id).then((d) => setItems(d?.items || [])); }
  useEffect(refresh, [project.id]);
  useEffect(() => { pmApi.issues({ project_id: project.id, status: 'done' }).then((d) => setIssues(d?.items || [])); }, [project.id]);

  function toggle(id) {
    setPickedIssues((s) => { const next = new Set(s); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }

  async function submit(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const created = await pmApi.createRelease({
      projectId: project.id,
      version: fd.get('version'),
      name: fd.get('name'),
      plannedDate: fd.get('plannedDate') || null,
      releaseNotes: fd.get('notes') || null,
      issueIds: [...pickedIssues],
    });
    setPickedIssues(new Set());
    setShowForm(false);
    e.currentTarget.reset();
    refresh();
    nav(`/pm/projects/${project.key}/releases/${created.release.id}`);
  }

  return (
    <section>
      {project && (
        <header className="page-header">
          <h1>{project.name} · Releases</h1>
          <button className="btn primary" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Close' : '+ New Release'}</button>
        </header>
      )}

      {showForm && (
        <form onSubmit={submit} className="card form-card">
          <div className="grid-form">
            <label>Version<input name="version" required placeholder="e.g. 1.4.0" /></label>
            <label>Name<input name="name" required placeholder="e.g. Q2 Feature Drop" /></label>
            <label>Planned date<input name="plannedDate" type="date" /></label>
            <label className="full">Release notes<textarea name="notes" rows={3} /></label>
          </div>
          <h4>Pick issues for this release ({pickedIssues.size} selected)</h4>
          <div className="release-issue-pick">
            {issues.map((i) => (
              <label key={i.id} className={`vendor-pick ${pickedIssues.has(i.id) ? 'picked' : ''}`}>
                <input type="checkbox" checked={pickedIssues.has(i.id)} onChange={() => toggle(i.id)} />
                <span><code>{i.full_key}</code> {i.title}</span>
              </label>
            ))}
            {issues.length === 0 && <p className="muted">No "done" issues yet to ship.</p>}
          </div>
          <div className="actions"><button className="btn primary" type="submit">Create release</button></div>
        </form>
      )}

      <div className="card">
        <table className="data-table">
          <thead><tr><th>Version</th><th>Name</th><th>Planned</th><th>Actual</th><th>Status</th></tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id}>
                <td><Link to={`/pm/projects/${project.key}/releases/${r.id}`}><code>{r.version}</code></Link></td>
                <td>{r.name}</td>
                <td>{r.planned_date || '—'}</td>
                <td>{r.actual_date || '—'}</td>
                <td><span className={`chip status-${r.status}`}>{r.status}</span></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="muted">No releases yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ReleaseDetail({ releaseId, project }) {
  const [release, setRelease] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [deployments, setDeployments] = useState([]);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  function reload() {
    pmApi.getRelease(releaseId).then((d) => setRelease(d?.release));
    pmApi.releaseReadiness(releaseId).then(setReadiness).catch(() => setReadiness(null));
    pmApi.deployments(releaseId).then((d) => setDeployments(d?.items || []));
  }
  useEffect(reload, [releaseId]);

  async function deploy(env) {
    setError(null); setInfo(null);
    try {
      const dep = await pmApi.deploy(releaseId, { environment: env });
      setInfo(`Deploying to ${env}…`);
      // Demo: auto-complete after 1.5s success.
      setTimeout(async () => {
        try {
          await pmApi.completeDeployment(dep.deployment.id, { success: true });
          setInfo(`Deployment to ${env} succeeded`);
          reload();
        } catch (err) {
          setError(err.response?.data?.message || 'Failed to complete');
        }
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Deployment failed');
    }
  }

  if (!release) return <div className="muted">Loading…</div>;

  const lastByEnv = (env) => deployments.find((d) => d.environment === env);

  return (
    <section>
      <header className="page-header">
        <div>
          <h1>{release.name} · <code>{release.version}</code></h1>
          <p className="muted">
            Planned {release.planned_date || 'TBD'} ·{' '}
            <span className={`chip status-${release.status}`}>{release.status}</span>
          </p>
        </div>
        <Link to={`/pm/projects/${project?.key || release.project_id}/releases`} className="btn ghost">← All releases</Link>
      </header>

      {error && <div className="alert error">{error}</div>}
      {info && <div className="alert info">{info}</div>}

      <div className="grid-2">
        <div className="card">
          <h3>Readiness</h3>
          {!readiness ? <div className="muted">Loading…</div> : (
            <>
              <div className="readiness-row">
                <ProgressDonut value={readiness.passing} total={readiness.total || 1} color="#16a34a" label="Passing" />
                <ProgressDonut value={readiness.failing} total={readiness.total || 1} color="#dc2626" label="Failing" />
                <ProgressDonut value={readiness.untested} total={readiness.total || 1} color="#94a3b8" label="Untested" />
              </div>
              <p className={`alert ${readiness.readyToRelease ? 'info' : 'error'}`} style={{ marginTop: 12 }}>
                {readiness.readyToRelease ? 'Ready to release ✓' : 'Some issues are failing or untested.'}
              </p>
              <h4>Issues ({readiness.byIssue?.length || 0})</h4>
              <ul className="trace-list">
                {(readiness.byIssue || []).map((i) => (
                  <li key={i.id}>
                    <span className={`chip status-${i.qa_status === 'pass' ? 'approved' : i.qa_status === 'fail' ? 'rejected' : 'pending'}`}>{i.qa_status}</span>
                    <code>{i.project_key}-{i.issue_no}</code> {i.title}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="card">
          <h3>Deployments</h3>
          {['dev', 'staging', 'production'].map((env) => {
            const last = lastByEnv(env);
            return (
              <div key={env} className="deploy-row">
                <div>
                  <strong>{env}</strong>
                  {last ? (
                    <span className={`chip status-${last.status === 'success' ? 'approved' : last.status === 'failed' ? 'rejected' : 'pending'}`}>{last.status}</span>
                  ) : (
                    <span className="muted small">never deployed</span>
                  )}
                  {last?.deployed_at && <div className="muted small">{new Date(last.deployed_at).toLocaleString()}</div>}
                </div>
                <button
                  className="btn primary small"
                  onClick={() => deploy(env)}
                  disabled={release.status !== 'approved' && release.status !== 'released'}
                >
                  Deploy
                </button>
              </div>
            );
          })}
          {release.status !== 'approved' && release.status !== 'released' && (
            <p className="muted small">Release must be approved before deploying.</p>
          )}
        </div>
      </div>

      {release.release_notes && (
        <div className="card">
          <h3>Release notes</h3>
          <pre className="desc">{release.release_notes}</pre>
        </div>
      )}
    </section>
  );
}

function ProgressDonut({ value, total, color, label }) {
  const r = 28, c = 2 * Math.PI * r;
  const fraction = total ? value / total : 0;
  const dash = c * fraction;
  return (
    <div className="r-donut">
      <svg width="80" height="80">
        <circle cx="40" cy="40" r={r} fill="none" stroke="#e2e8f0" strokeWidth="8" />
        <circle cx="40" cy="40" r={r} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={`${dash} ${c}`} transform="rotate(-90 40 40)" strokeLinecap="round" />
        <text x="40" y="44" textAnchor="middle" fontSize="14" fontWeight="700">{value}</text>
      </svg>
      <span className="muted small">{label}</span>
    </div>
  );
}
